"use client";
import React, { useState } from "react";
import Link from "next/link";
import AuthButton, { MenuAuthButton } from "@/auth/AuthButton";
import useAuthorization from "@/auth/useAuth";
import SpinnerCheck from "@/components/spinner-check";
import {
  Headset,
  Users,
  Package,
  ChevronLeft,
  UserPlus,
  UserPen,
  UserX,
  UserIcon,
  Laptop,
  Mail,
  Monitor,
} from "lucide-react";
import InfoLinkDialog from "@/components/InfoLinkDialog";
import CancelButton from "@/components/cancel-button";

export default function Home() {
  const { session, isLoaded, isUserManager } = useAuthorization();
  const [showUserMenu, setShowUserMenu] = useState(false);

  if (!isLoaded && !session)
    return (
      <div className="w-full h-full flex flex-col flex-1 items-center justify-center">
        <SpinnerCheck size={"125px"} />
        <h2 className="my-4 text-2xl font-semibold">Logging in...</h2>
      </div>
    );

  if (!session)
    return (
      <div className="my-auto flex h-full w-full flex-col items-center justify-center space-y-6 pb-20">
        <div className="text-center">
          <h1 className="text-5xl font-semibold">Welcome!</h1>
          <h4 className="mt-2 text-lg font-semibold">
            Please sign in to continue
          </h4>
        </div>
        <div className="w-full flex flex-col items-center">
          <div className="w-1/2">
            <MenuAuthButton />
          </div>
        </div>
      </div>
    );

  if (showUserMenu && isUserManager) return <UserSubMenu />;

  return (
    <div className="flex flex-col justify-center items-center h-full px-6 mb-0 pb-0">
      <div className="text-center mb-4">
        <h1 className="text-4xl font-semibold">
          Welcome {session?.user.givenName}!
        </h1>
        <h4 className="text-lg font-semibold">
          What would you like to get started on?
        </h4>
      </div>

      <div className="flex flex-col gap-4 p-8 mx-auto min-h-[300px] justify-center items-center">
        {/* Support Request Card */}
        <Link href="/support">
          <div className="flex items-center w-[450px] border rounded-xl p-4 hover:cursor-pointer hover:bg-gray-100 transform transition duration-200 hover:scale-105">
            <Headset className="w-10 h-12 ml-4 mr-6 text-primary" />
            <div className="flex-1">
              <p className="text-lg font-semibold">Support Request</p>
              <p className="text-sm text-muted-foreground">
                Request help from the Gazelle Support team
              </p>
            </div>
          </div>
        </Link>

        {/* User Forms */}
        {isUserManager && (
          <div
            onClick={() => setShowUserMenu(true)}
            className="flex items-center w-[450px] border rounded-xl p-4 hover:cursor-pointer hover:bg-gray-100 transform transition duration-200 hover:scale-105"
          >
            <Users className="w-10 h-12 ml-4 mr-6 text-primary" />
            <div className="flex-1">
              <p className="text-lg font-semibold">User Forms</p>
              <p className="text-sm text-muted-foreground">
                Create, update, or disable volunteers and staff
              </p>
            </div>
          </div>
        )}

        {/* Hardware Orders */}
        {isUserManager && (
          <Link href="/hardware-order">
            <div className="flex items-center w-[450px] border rounded-xl p-4 hover:cursor-pointer hover:bg-gray-100 transform transition duration-200 hover:scale-105">
              <Package className="w-10 h-12 ml-4 mr-6 text-primary" />
              <div className="flex-1">
                <p className="text-lg font-semibold">Hardware Orders</p>
                <p className="text-sm text-muted-foreground">
                  Order devices and accessories
                </p>
              </div>
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}

function UserSubMenu(): JSX.Element {
  return (
    <div className="flex flex-col justify-center h-full px-6 mb-0 pb-0">
      <div className="text-center mb-6">
        <h1 className="text-4xl font-semibold">User Account Requests</h1>
        <h4 className="text-lg font-semibold">
          What would you like to get started on?
        </h4>
      </div>

      {/* Two-column menu */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 justify-center">
        {/* Staff Users (left) */}
        <div className="flex flex-col gap-4 max-w-[350px] w-full">
          <Link href="/create">
            <div className="flex items-center border rounded-xl px-2 py-4 hover:bg-gray-100 transition hover:scale-105">
              <UserPlus className="w-8 h-8 ml-2 mr-4 text-primary" />
              <div>
                <p className="text-md font-semibold">New Volunteer</p>
                <p className="text-xs text-muted-foreground">
                  Request to create a volunteer user
                </p>
              </div>
            </div>
          </Link>
          <Link href="/update">
            <div className="flex items-center border rounded-xl px-2 py-4 hover:bg-gray-100 transition hover:scale-105">
              <UserPen className="w-8 h-8 ml-2 mr-4 text-primary" />
              <div>
                <p className="text-md font-semibold">Update Volunteer</p>
                <p className="text-xs text-muted-foreground">
                  Request updates to a volunteer user
                </p>
              </div>
            </div>
          </Link>
          <Link href="/disable">
            <div className="flex items-center border rounded-xl px-2 py-4 hover:bg-gray-100 transition hover:scale-105">
              <UserX className="w-8 h-8 ml-2 mr-4 text-primary" />
              <div>
                <p className="text-md font-semibold">Disable Volunteer(s)</p>
                <p className="text-xs text-muted-foreground">
                  Disable one or more users
                </p>
              </div>
            </div>
          </Link>
        </div>

        {/* Standard Users (right) */}
        <div className="flex flex-col gap-4 max-w-[350px] w-full">
          <Link href="/staff-create">
            <div className="flex items-center border rounded-xl px-2 py-4 hover:bg-gray-100 transition hover:scale-105">
              <UserPlus className="w-8 h-8 ml-2 mr-4 text-primary" />
              <div>
                <p className="text-md font-semibold">Create Staff User</p>
                <p className="text-xs text-muted-foreground">
                  Create staff user + upgrade volunteers
                </p>
              </div>
            </div>
          </Link>
          <Link href="/staff-update">
            <div className="flex items-center border rounded-xl px-2 py-4 hover:bg-gray-100 transition hover:scale-105">
              <UserPen className="w-8 h-8 ml-2 mr-4" />
              <div>
                <p className="text-md font-semibold">Update Staff User</p>
                <p className="text-xs text-muted-foreground">
                  Update an existing staff user
                </p>
              </div>
            </div>
          </Link>
          <Link href="/staff-disable">
            <div className="flex items-center border rounded-xl px-2 py-4 hover:bg-gray-100 transition hover:scale-105">
              <UserX className="w-8 h-8 ml-2 mr-4" />
              <div>
                <p className="text-md font-semibold">Disable Staff User</p>
                <p className="text-xs text-muted-foreground">
                  Disable an existing staff user
                </p>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* Info + Back */}
      <div className="flex flex-col justify-center mt-8 mx-auto mb-2">
        <div className="w-full flex justify-center pb-4">
          <InfoLinkDialog
            buttonLabel="What's the difference between Volunteers and Staff?"
            title="Difference between Volunteers and Staff"
          >
            <div className="grid gap-4 mt-2">
              <div className="p-1 mb-0">
                <p className="text-base text-muted-foreground leading-relaxed">
                  The primary differences between{" "}
                  <span className="font-medium">Volunteer</span> and{" "}
                  <span className="font-medium">Staff</span> users are roles in
                  Genesys, Hardware, Email Format and Microsoft Licensing.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* Volunteer */}
                <div className="rounded-xl border bg-background p-4">
                  <h4 className="text-lg font-semibold text-black mb-3">
                    Volunteer User
                  </h4>
                  <ul className="grid gap-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4" />
                      Agents or Mentors
                    </li>
                    <li className="flex items-center gap-2">
                      <Laptop className="w-4 h-4" />
                      Bring your own device
                    </li>
                    <li className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      @dcogt.help email
                    </li>
                    <li className="flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      365 Basic license
                    </li>
                  </ul>
                </div>

                {/* Staff */}
                <div className="rounded-xl border bg-background p-4">
                  <h4 className="text-lg font-semibold text-black mb-3">
                    Staff User
                  </h4>
                  <ul className="grid gap-2 text-sm text-muted-foreground">
                    <li className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4" />
                      Supervisor
                    </li>
                    <li className="flex items-center gap-2">
                      <Laptop className="w-4 h-4" />
                      Hardware order
                    </li>
                    <li className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      @dcogt.com email
                    </li>
                    <li className="flex items-center gap-2">
                      <Monitor className="w-4 h-4" />
                      365 Premium license
                    </li>
                  </ul>
                </div>
              </div>
              <div className="pb-2">
                <Link href="/staff-create" passHref>
                  <div className="bg-yellow-100 border-l-4 border-yellow-300 text-yellow-900 p-4 rounded-xl shadow-sm hover:shadow-md transition-all cursor-pointer">
                    <h3 className="text-lg font-bold mb-1">
                      Trying to upgrade a Volunteer to Staff?
                    </h3>
                    <p className="text-md text-muted-foreground">
                      Use the Create Staff User form to upgrade a volunteer to
                      staff. Select <span className="font-semibold">Yes</span>{" "}
                      under{" "}
                      <span className="font-semibold">
                        Any existing DCOGT accounts?
                      </span>
                      , then enter the existing volunteer email in the
                      corresponding field.
                    </p>
                  </div>
                </Link>
              </div>
            </div>
          </InfoLinkDialog>
        </div>

        <div className="mx-auto mb-2">
          <CancelButton variant="outline">
            <ChevronLeft /> Go Back
          </CancelButton>
        </div>
      </div>
    </div>
  );
}
